import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { WheelSlot, WheelSpinResult } from "../../types/wheel-of-luck";
import { RewardPresentation, rewardLevel } from "../../hooks/useWheelRewardCatalog";
import { DEFAULT_WHEEL_ART, wheelArt, wheelRotation } from "../../utils/wheel-of-luck";
import { ApiUrl } from "../../utils/Network";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import "./wheel.css";

type Props = {
  // The wheel's skin pictures from the server; without them the built-in art is drawn.
  wheelImage?: string;
  pointerImage?: string;
  slots: WheelSlot[];
  result: WheelSpinResult | null;
  describe: (reward: WheelSlot) => RewardPresentation;
  onComplete: () => void;
};

export default function LuckyWheel({ wheelImage, pointerImage, slots, result, describe, onComplete }: Props) {
  const { t, currentLanguage } = useAppTranslation(["wheel"]);
  const [rotation, setRotation] = useState(0);
  // A skin picture that does not load (a host without it yet) falls back to the
  // built-in art for both pictures, so the pointer never sits on another wheel.
  const [failedArt, setFailedArt] = useState<string>();
  const served = wheelArt({ wheelImage, pointerImage }, ApiUrl);
  const art = served.sharedCanvas && failedArt === served.wheel ? DEFAULT_WHEEL_ART : served;
  const artFailed = () => { if (art.sharedCanvas) setFailedArt(art.wheel); };
  const processed = useRef<WheelSpinResult | null>(null);
  const targetRotation = useRef(0);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (!result || result === processed.current) return;
    processed.current = result;
    targetRotation.current = wheelRotation(targetRotation.current, result.winningSlotIndex);
    setRotation(targetRotation.current);
  }, [result]);

  return (
    <div className="luck-wheel" aria-label={t("wheel:wheelLabel")}>
      <motion.div className="luck-wheel-rotor" animate={{ rotate: rotation }}
        transition={{ duration: reduceMotion ? 0 : 3.5, ease: [0.12, 0.8, 0.18, 1] }}
        onAnimationComplete={definition => {
          if (result && processed.current === result && typeof definition === "object" && !Array.isArray(definition) &&
            definition.rotate === targetRotation.current) onComplete();
        }}>
        <img className="luck-wheel-art" src={art.wheel} alt="" onError={artFailed} />
        {slots.map((slot, index) => slot.claimed && <div key={index} aria-hidden="true"
          className="luck-wheel-claimed-segment" style={{ transform: `rotate(${index * 45}deg)` }} />)}
        <ol className="luck-wheel-slots">
          {slots.map((slot, index) => {
            const reward = describe(slot);
            const level = rewardLevel(slot);
            const angle = index * Math.PI / 4;
            const quantity = slot.quantity.toLocaleString(currentLanguage);
            let label = t("wheel:slot.reward", { reward: reward.name, quantity });
            if (level > 0) label = t("wheel:slot.withLevel", { label, level: level.toLocaleString(currentLanguage) });
            if (slot.claimed) label = t("wheel:slot.claimed", { label });
            return (
              <li key={index} className={`luck-wheel-slot${slot.claimed ? " is-claimed" : ""}`}
                style={{ left: `${50 + Math.sin(angle) * 27}%`, top: `${50 - Math.cos(angle) * 27}%` }}
                aria-label={label}>
                <motion.div className="luck-wheel-prize" animate={{ rotate: -rotation }}
                  transition={{ duration: reduceMotion ? 0 : 3.5, ease: [0.12, 0.8, 0.18, 1] }}>
                  {reward.icon ? <img src={reward.icon} alt={reward.name} /> : <span className="luck-prize-name">{reward.name}</span>}
                  {level > 0 && <span className="luck-prize-level">{level}</span>}
                  <span className="luck-prize-quantity">{quantity}</span>
                  {slot.claimed && <span className="luck-prize-claimed">{t("wheel:claimed")}</span>}
                </motion.div>
              </li>
            );
          })}
        </ol>
      </motion.div>
      <img className={`luck-wheel-frame${art.sharedCanvas ? " luck-wheel-frame--canvas" : ""}`} src={art.pointer} alt="" onError={artFailed} />
    </div>
  );
}
